// shared/src/hydraulic/scenarios/mutations.ts
import type { Scenarios, ScenarioNode, Override, OverrideField } from './schema'

/** All transitive descendant ids of `scenarioId` (excludes self). */
export function getDescendantIds(nodes: ScenarioNode[], scenarioId: string): string[] {
  const childrenOf = new Map<string, string[]>()
  for (const n of nodes) {
    if (n.parent_id !== null) {
      const list = childrenOf.get(n.parent_id) ?? []
      list.push(n.id)
      childrenOf.set(n.parent_id, list)
    }
  }
  const out: string[] = []
  const stack = [...(childrenOf.get(scenarioId) ?? [])]
  while (stack.length) {
    const id = stack.pop()!
    out.push(id)
    stack.push(...(childrenOf.get(id) ?? []))
  }
  return out
}

/** Drop every override whose target id is in `removedIds`, across all nodes.
 * Returns a new Scenarios (input untouched). */
export function pruneOverridesForRemovedIds(scenarios: Scenarios, removedIds: Set<string>): Scenarios {
  return {
    ...scenarios,
    nodes: scenarios.nodes.map((n) => ({
      ...n,
      overrides: n.overrides.filter((o) => !removedIds.has(o.id)),
    })),
  }
}

/** Insert/replace an override (matched by id+field) on node `scenarioId`. */
export function upsertOverride(scenarios: Scenarios, scenarioId: string, override: Override): Scenarios {
  return {
    ...scenarios,
    nodes: scenarios.nodes.map((n) => {
      if (n.id !== scenarioId) return n
      const overrides = n.overrides.filter((o) => !(o.target === override.target && o.id === override.id && o.field === override.field))
      overrides.push(override)
      return { ...n, overrides }
    }),
  }
}

/** Remove the override matching (id, field) on node `scenarioId` (reset-to-inherited).
 * Matches on (id, field) without `target`: this is sufficient today because no
 * OverrideField is valid on both a vertex and an edge (edges only allow `pipe_spec`),
 * so (id, field) uniquely implies the target. Add a `target` param if that ever changes. */
export function removeOverride(scenarios: Scenarios, scenarioId: string, id: string, field: OverrideField): Scenarios {
  return {
    ...scenarios,
    nodes: scenarios.nodes.map((n) =>
      n.id === scenarioId
        ? { ...n, overrides: n.overrides.filter((o) => !(o.id === id && o.field === field)) }
        : n,
    ),
  }
}

/** Add a new child scenario (no overrides) under `parentId`. Throws on duplicate id. */
export function branchScenario(scenarios: Scenarios, parentId: string, name: string, newId: string): Scenarios {
  if (scenarios.nodes.some((n) => n.id === newId)) {
    throw new Error(`Scenario id '${newId}' already exists`)
  }
  if (!scenarios.nodes.some((n) => n.id === parentId)) {
    throw new Error(`Parent scenario '${parentId}' does not exist`)
  }
  return {
    ...scenarios,
    nodes: [...scenarios.nodes, { id: newId, name, parent_id: parentId, overrides: [] }],
  }
}

/**
 * Delete scenario `scenarioId`, re-parenting its immediate children to its
 * parent. To keep each surviving child's resolved system identical, the deleted
 * node's OWN overrides are baked into each immediate child for every field that
 * child does not already pin (P's overrides are absolute top-level values, so a
 * verbatim copy is correct; deeper descendants are handled by cascade through
 * the now-baked child). Root cannot be deleted.
 *
 * Invariant: for every surviving node N, resolveScenario(after,N) === resolveScenario(before,N).
 */
export function deleteScenarioWithBake(scenarios: Scenarios, scenarioId: string): Scenarios {
  const target = scenarios.nodes.find((n) => n.id === scenarioId)
  if (!target) throw new Error(`Scenario '${scenarioId}' does not exist`)
  if (target.parent_id === null) throw new Error('Cannot delete the root (Base) scenario')

  const newParentId = target.parent_id

  const nodes = scenarios.nodes
    .filter((n) => n.id !== scenarioId)
    .map((n) => {
      if (n.parent_id !== scenarioId) return n
      // Immediate child of the deleted node: re-parent + bake unpinned fields.
      const pinned = new Set(n.overrides.map((o) => `${o.target}:${o.id}:${o.field}`))
      const baked: Override[] = target.overrides.filter(
        (o) => !pinned.has(`${o.target}:${o.id}:${o.field}`),
      )
      return { ...n, parent_id: newParentId, overrides: [...n.overrides, ...baked] }
    })

  const active_id = scenarios.active_id === scenarioId ? newParentId : scenarios.active_id
  return { active_id, nodes }
}
